<script lang="ts">
    import {
        FeedParticipantFragmentDoc,
        LinkMetaDataFragmentDoc,
        MediaImageFragmentDoc,
        ParticipantAvatarFragmentDoc,
        ParticipantCardFragmentDoc,
        type RelatedFeedPostFragment,
    } from '$gql/graphql.js';
    import { cx } from '$lib/utils/cx.js';
    import truncate from 'lodash/truncate';
    import filter from 'lodash/filter';
    import FeedPostMediaCollection from './FeedPostMediaCollection.svelte';
    import { useFragment } from '$gql/fragment-masking.js';
    import { FEED_POST_BODY_MAX_LENGTH } from './Utils';
    import ParticipantAvatar from '../ParticipantAvatar/ParticipantAvatar.svelte';
    import ParticipantCard from '$components/ParticipantCard/ParticipantCard.svelte';
    import { getContainer } from '$components/Context/ContainerContext.js';
    import MentionText from './MentionText.svelte';
    import LinkMetaData from '$components/LinkMetaData/LinkMetaData.svelte';
    import { fromZonedDateTimeStringToLocalDate } from '$lib/utils/zonedDateTime';
    import { nullIfEmptyString } from '$lib/utils/nullIfEmptyString';
    import * as t from '$trans';

    const { intl, svelteClient, lang } = getContainer();
    const { goto } = svelteClient;

    export let post: RelatedFeedPostFragment;
    let className: string = '';
    export { className as class };

    let fullTextMode: boolean = false;

    function redirectListener(e: MouseEvent) {
        const target = e.target as HTMLElement;
        if (
            e.currentTarget === target ||
            (target.tagName !== 'BUTTON' && target.tagName !== 'A')
        ) {
            goto(post.url);
        }
    }

    function redirectListenerOnEnter(e: KeyboardEvent) {
        const target = e.target as HTMLElement;
        if (
            e.currentTarget === target ||
            (target.tagName !== 'BUTTON' && target.tagName !== 'A')
        ) {
            if (e.key === 'Enter') {
                goto(post.url);
            }
        }
    }

    $: author = useFragment(FeedParticipantFragmentDoc, post.author);
    $: linkMetaDatas = post.linkMetaDatas.map((l) =>
        useFragment(LinkMetaDataFragmentDoc, l),
    );
    $: medias = post.medias.map((m) => useFragment(MediaImageFragmentDoc, m));

    function getText(text: string, showAll: boolean) {
        if (text == null) return;
        if (showAll) {
            return text;
        } else {
            return truncate(text, { length: FEED_POST_BODY_MAX_LENGTH });
        }
    }

    $: text = getText(post?.body, fullTextMode);
    $: createdDate = fromZonedDateTimeStringToLocalDate(post.createdDate).toDate();

    $: authorTitle =
        author.__typename === 'User'
            ? author.participantTitle
            : `${intl().countryLabel(author.country)}${
                  author.country !== null ? ' > ' : ''
              }${author.category.category}`;
</script>

<div
    on:click={redirectListener}
    on:keypress={redirectListenerOnEnter}
    role="button"
    tabindex={0}
    class={cx(
        className,
        'cursor-pointer my-[12px] bg-white p-[12px] border border-solid rounded-[16px] border-gray-300',
        'hover:shadow hover:border-brand-blue',
        'focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue',
    )}
>
    <div class="grid-header mb-8">
        <ParticipantCard
            class="[grid-area:avatar]"
            participant={useFragment(ParticipantCardFragmentDoc, author)}
        >
            <ParticipantAvatar
                participant={useFragment(ParticipantAvatarFragmentDoc, author)}
            />
        </ParticipantCard>

        <ParticipantCard
            class="[grid-area:name] inline truncate"
            participant={useFragment(ParticipantCardFragmentDoc, author)}
        >
            <a
                href={author.participantProfileLink}
                data-sveltekit-reload
                class="text-12 font-bold truncate"
            >
                {author.participantName}
            </a>
        </ParticipantCard>

        {#if nullIfEmptyString(authorTitle) !== null}
            <a
                href={post.url}
                data-sveltekit-reload={false}
                class={'[grid-area:title] italic text-12 text-gray-600 truncate no-underline'}
            >
                {authorTitle}
            </a>
        {/if}

        <a
            href={post.url}
            data-sveltekit-reload={false}
            class="[grid-area:date] italic text-12 text-gray-700 hover:text-gray-700 no-underline"
        >
            <div class="no-underline italic text-12 text-gray-600">
                {intl().formatDistance(createdDate, new Date(), {
                    addSuffix: true,
                })}
            </div>
        </a>
    </div>

    {#if post.title}
        <h4>{post.title}</h4>
    {/if}
    <div class="text-14 mb-4">
        <div>
            <MentionText {text} />
        </div>
        {#if post.body.length > FEED_POST_BODY_MAX_LENGTH}
            <button
                on:click|preventDefault={() => (fullTextMode = !fullTextMode)}
                class={'btn-reset block w-full text-right text-link cursor-pointer hover:text-link-hover hover:underline'}
            >
                {fullTextMode
                    ? t.feed_post.see_less({}, $lang)
                    : t.feed_post.see_more({}, $lang)}
            </button>
        {/if}
    </div>
    <FeedPostMediaCollection {medias} />
    {#each filter(linkMetaDatas, (linkMetaData) => linkMetaData.relatedFeedPost === null) as linkMetaData (linkMetaData.id)}
        <LinkMetaData {linkMetaData} />
    {/each}
</div>

<style>
    .grid-header {
        display: grid;
        grid-template:
            'avatar         name' auto
            'avatar         title' auto
            'avatar         date' auto
            / max-content 1fr;

        column-gap: 12px;
        row-gap: 0;
    }
</style>
